import { computed, onMounted, onUnmounted, toValue, watch, type MaybeRefOrGetter } from 'vue'
import fetchApi from '@/utils/fetchApi'
import { useLivekitCore } from './useLivekitCore'

const ROOM_STATUS_POLL_INTERVAL_MS = 5_000

interface RoomStatusesResponse {
  statuses: Record<string, boolean>
}

export const useLivekitRoomStatusPolling = (
  roomNamesSource: MaybeRefOrGetter<string[]>,
  intervalMs = ROOM_STATUS_POLL_INTERVAL_MS,
) => {
  const { disconnectedRooms } = useLivekitCore()
  let intervalId: number | undefined
  let requestInFlight = false
  let hasReportedError = false

  const roomNames = computed(() => [
    ...new Set(
      toValue(roomNamesSource).filter(
        (roomName) => roomName.length > 0 && !disconnectedRooms.value.has(roomName),
      ),
    ),
  ])

  const syncRoomStatuses = async () => {
    if (requestInFlight || roomNames.value.length === 0) return

    requestInFlight = true
    try {
      const { statuses } = await fetchApi<RoomStatusesResponse>(
        {
          endpoint: '/api/livekit/room-statuses',
          method: 'POST',
          body: { roomNames: roomNames.value },
        },
        { suppressAlert: true },
      )
      hasReportedError = false

      for (const [roomName, active] of Object.entries(statuses)) {
        if (!active) disconnectedRooms.value.add(roomName)
      }
    } catch (error) {
      if (!hasReportedError) {
        console.error('❌ Failed to refresh LiveKit room statuses:', error)
        hasReportedError = true
      }
    } finally {
      requestInFlight = false
    }
  }

  watch(roomNames, () => void syncRoomStatuses(), { immediate: true })

  onMounted(() => {
    intervalId = window.setInterval(() => void syncRoomStatuses(), intervalMs)
  })

  onUnmounted(() => {
    if (intervalId !== undefined) window.clearInterval(intervalId)
  })

  return { syncRoomStatuses }
}
